import API from "../context/API";

/**
 * Earnings API Service
 * Handles merchant earnings data fetching with enhanced fee breakdown
 */

// Base earnings API configuration
const EARNINGS_ENDPOINTS = {
  MERCHANT_EARNINGS: '/merchant/{merchantId}/earnings',
  FEE_BREAKDOWN: '/analytics/fee-breakdown',
  ADMIN_MERCHANT_EARNINGS: '/admin/merchant-earnings',
  ADMIN_MERCHANT_EARNINGS_EXPORT: '/admin/merchant-earnings/export'
};

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryMultiplier: 2 // Exponential backoff
};

/**
 * Generic API call with retry logic for earnings
 * @param {string} endpoint - API endpoint
 * @param {object} params - Query parameters
 * @param {object} options - Additional options
 * @returns {Promise} API response
 */
const apiCallWithRetry = async (endpoint, params = {}, options = {}) => {
  const { maxRetries, retryDelay, retryMultiplier } = RETRY_CONFIG;
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const token = localStorage.getItem("access_token");
      
      if (!token) {
        throw new Error("No access token found");
      }

      const response = await API.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          ...params,
          _t: Date.now(), // Cache busting
        },
        ...options
      });

      // Validate response structure
      if (!response.data) {
        throw new Error("Invalid response structure: missing data");
      }

      return response;
    } catch (error) {
      lastError = error;
      
      // Don't retry on authentication errors
      if (error.response?.status === 401) {
        throw error;
      }

      // Don't retry on client errors (4xx) except 408, 429
      if (error.response?.status >= 400 && error.response?.status < 500 && 
          error.response?.status !== 408 && error.response?.status !== 429) {
        throw error;
      }

      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        throw error;
      }

      // Wait before retrying with exponential backoff
      const delay = retryDelay * Math.pow(retryMultiplier, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      console.warn(`Earnings API call failed, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
    }
  }

  throw lastError;
};

/**
 * Build parameters for earnings requests
 * @param {object} options - Request options
 * @returns {object} Formatted parameters
 */
const buildEarningsParams = (options = {}) => {
  const {
    startDate,
    endDate,
    merchantId,
    store,
    includeComparison = false,
    ...otherParams
  } = options;

  const params = {};

  // Date range parameters
  if (startDate && endDate) {
    params.startDate = typeof startDate === 'string' ? startDate : startDate.format('YYYY-MM-DD');
    params.endDate = typeof endDate === 'string' ? endDate : endDate.format('YYYY-MM-DD');
  }

  // Entity parameters
  if (merchantId) {
    params.merchantId = merchantId;
  }
  if (store) {
    params.store = store;
  }

  // Comparison parameter
  if (includeComparison) {
    params.includeComparison = includeComparison;
  }

  // Add any other parameters
  Object.assign(params, otherParams);

  return params;
};

/**
 * Earnings API Service Class
 */
class EarningsAPI {
  /**
   * Fetch merchant earnings with comprehensive breakdown
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} Merchant earnings data
   */
  static async getMerchantEarnings(merchantId, options = {}) {
    if (!merchantId) {
      throw new Error("Merchant ID is required");
    }

    const endpoint = EARNINGS_ENDPOINTS.MERCHANT_EARNINGS.replace('{merchantId}', merchantId);
    const params = buildEarningsParams(options);
    const response = await apiCallWithRetry(endpoint, params);
    return response.data;
  }

  /**
   * Fetch fee breakdown analytics
   * @param {object} options - Request options
   * @returns {Promise} Fee breakdown data
   */
  static async getFeeBreakdown(options = {}) {
    const params = buildEarningsParams(options);
    const response = await apiCallWithRetry(EARNINGS_ENDPOINTS.FEE_BREAKDOWN, params);
    return response.data;
  }

  /**
   * Fetch admin merchant earnings (paginated, sortable, filterable)
   * @param {object} options - { startDate, endDate, merchantId?, storeId?, page, limit, sortBy, sortOrder }
   * @returns {Promise} Admin merchant earnings overview
   */
  static async getAdminMerchantEarnings(options = {}) {
    const params = buildEarningsParams(options);
    const response = await apiCallWithRetry(EARNINGS_ENDPOINTS.ADMIN_MERCHANT_EARNINGS, params);
    return response.data;
  }

  /**
   * Export admin merchant earnings CSV
   * @param {object} options - same filters as getAdminMerchantEarnings
   * @returns {Promise<string>} CSV text
   */
  static async exportAdminMerchantEarningsCSV(options = {}) {
    const params = buildEarningsParams(options);
    const token = localStorage.getItem("access_token");
    if (!token) {
      throw new Error("No access token found");
    }

    const response = await API.get(EARNINGS_ENDPOINTS.ADMIN_MERCHANT_EARNINGS_EXPORT, {
      headers: { Authorization: `Bearer ${token}` },
      params,
      responseType: 'text'
    });
    return response.data;
  }

  /**
   * Get platform breakdown (kiosk vs mobile)
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} Platform breakdown data
   */
  static async getPlatformBreakdown(merchantId, options = {}) {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    return earnings.data?.platformBreakdown || {};
  }

  /**
   * Get fulfillment breakdown (pickup vs delivery)
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} Fulfillment breakdown data
   */
  static async getFulfillmentBreakdown(merchantId, options = {}) {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    return earnings.data?.fulfillmentBreakdown || {};
  }

  /**
   * Get fee categories with Stripe fees highlighted
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} Fee categories data
   */
  static async getFeeCategories(merchantId, options = {}) {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    return earnings.data?.feeCategories || {};
  }

  /**
   * Get Stripe fees specifically
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} Stripe fees data
   */
  static async getStripeFees(merchantId, options = {}) {
    const feeCategories = await this.getFeeCategories(merchantId, options);
    return feeCategories.stripe || { amount: 0, count: 0, percentage: 0 };
  }

  /**
   * Calculate net earnings (revenue - fees)
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} Net earnings calculation
   */
  static async getNetEarnings(merchantId, options = {}) {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    const summary = earnings.data?.summary || {};
    
    return {
      totalRevenue: summary.totalRevenue || 0,
      totalFees: summary.totalFees || 0,
      netEarnings: summary.netEarnings || 0,
      feePercentage: summary.totalRevenue > 0 ? 
        ((summary.totalFees || 0) / summary.totalRevenue * 100) : 0
    };
  }

  /**
   * Get earnings summary for dashboard display
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} Dashboard summary data
   */
  static async getDashboardSummary(merchantId, options = {}) {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    const data = earnings.data || {};
    
    return {
      merchantName: data.merchantName || 'Unknown',
      period: data.period || {},
      summary: data.summary || {},
      platformBreakdown: data.platformBreakdown || {},
      fulfillmentBreakdown: data.fulfillmentBreakdown || {},
      feeCategories: data.feeCategories || {}
    };
  }

  /**
   * Export earnings data to CSV format
   * @param {string} merchantId - Merchant ID
   * @param {object} options - Request options
   * @returns {Promise} CSV formatted data
   */
  static async exportEarningsCSV(merchantId, options = {}) {
    const earnings = await this.getMerchantEarnings(merchantId, options);
    const data = earnings.data || {};
    
    // Create CSV headers
    const headers = [
      'Date Range',
      'Merchant Name',
      'Total Revenue',
      'Total Fees',
      'Net Earnings',
      'Order Count',
      'Average Order Value',
      'Kiosk Orders',
      'Kiosk Revenue',
      'Mobile Orders',
      'Mobile Revenue',
      'Pickup Orders',
      'Pickup Revenue',
      'Delivery Orders',
      'Delivery Revenue',
      'Stripe Fees',
      'Processing Fees',
      'Tax',
      'Tips',
      'Discounts'
    ];

    // Create CSV rows
    const rows = [
      [
        `${data.period?.startDate || ''} to ${data.period?.endDate || ''}`,
        data.merchantName || '',
        data.summary?.totalRevenue || 0,
        data.summary?.totalFees || 0,
        data.summary?.netEarnings || 0,
        data.summary?.orderCount || 0,
        data.summary?.averageOrderValue || 0,
        data.platformBreakdown?.kiosk?.orderCount || 0,
        data.platformBreakdown?.kiosk?.revenue || 0,
        data.platformBreakdown?.mobile?.orderCount || 0,
        data.platformBreakdown?.mobile?.revenue || 0,
        data.fulfillmentBreakdown?.pickup?.orderCount || 0,
        data.fulfillmentBreakdown?.pickup?.revenue || 0,
        data.fulfillmentBreakdown?.delivery?.orderCount || 0,
        data.fulfillmentBreakdown?.delivery?.revenue || 0,
        data.feeCategories?.stripe?.amount || 0,
        data.feeCategories?.processing?.amount || 0,
        data.feeCategories?.tax?.amount || 0,
        data.feeCategories?.tips?.amount || 0,
        data.feeCategories?.discounts?.amount || 0
      ]
    ];

    // Convert to CSV string
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  }
}

export default EarningsAPI;
export { EARNINGS_ENDPOINTS, RETRY_CONFIG };

